/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

// Needed for redux-saga es6 generator support
import "babel-polyfill";

// Import all the third party stuff
import React from "react";
import ReactDOM from "react-dom";
import { ThemeProvider } from "@mui/material/styles";
import { Provider } from "react-redux";
import { applyRouterMiddleware, Router, browserHistory } from "react-router";
import { syncHistoryWithStore } from "react-router-redux";
import { useScroll } from "react-router-scroll";
import "sanitize.css/sanitize.css";
import packageJson from '../package.json';

// Import selectors for `syncHistoryWithStore`
import { makeSelectLocationState } from "containers/App/selectors";
// import { getLanguage } from 'utils/helpers';
// Import Language Provider
import LanguageProvider from "containers/LanguageProvider";
// import { changeLocale } from 'containers/LanguageProvider/actions';

import configureStore from "./store";

// Import i18n messages
import { translationMessages } from "./i18n";

// Import CSS reset and Global Styles
import "./global-styles";

import "./theme/fonts/lato.css";

// Import root routes
import { createRoutes } from "./routes";

// Import material theme
import configureTheme from "./theme/configureTheme";
import GlobalStyle from "./global-styles";

// Create redux store with history
// this uses the singleton browserHistory provided by react-router
// Optionally, this could be changed to leverage a created history
// e.g. `const browserHistory = useRouterHistory(createBrowserHistory)();`
const initialState = {};
const store = configureStore(initialState, browserHistory);

// Sync history and store, as the react-router-redux reducer
// is under the non-default key ("routing"), selectLocationState
// must be provided for resolving how to retrieve the "route" in the state
const history = syncHistoryWithStore(browserHistory, store, {
  selectLocationState: makeSelectLocationState(),
});

// Set up the router, wrapping all Routes in the App component
const rootRoutes = createRoutes(store);

const render = (messages) => {
  ReactDOM.render(
    <Provider store={store}>
      <ThemeProvider theme={configureTheme}>
        <GlobalStyle />
        <LanguageProvider messages={messages}>
          <Router
            history={history}
            routes={rootRoutes}
            render={
              // Scroll to top when going to a new page, imitating default browser
              // behaviour
              // eslint-disable-next-line react-hooks/rules-of-hooks
              applyRouterMiddleware(useScroll())
            }
          />
        </LanguageProvider>
      </ThemeProvider>
    </Provider>,
    document.getElementById("app")
  );
};

// Hot reloadable translation json files. its for hot updates on i18n files (en.json, etc…)
// documentation: https://webpack.js.org/guides/hot-module-replacement
if (module.hot) {
  // modules.hot.accept does not accept dynamic dependencies,
  // have to be constants at compile-time
  module.hot.accept("./i18n", () => {
    translationMessages().then(render);
  });
}

// Chunked polyfill for browsers without Intl support
// documentation: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl
if (!window.Intl) {
  new Promise((resolve) => {
    resolve(import("intl"));
  })
    .then(() => Promise.all([import("intl/locale-data/jsonp/en.js")]))
    .then(() => {
      translationMessages().then(render);
    })
    .catch((err) => {
      throw err;
    });
} else {
  translationMessages().then(render);
}

console.info(`RWS v${packageJson.version}`)
